import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { createPresenceSocket, disconnectPresenceSocket } from "config/presenceSocket";
import { getToken } from "config/storageController";

const PresenceContext = createContext({
  isConnected: false,
  error: null,
  reconnect: () => {},
});

PresenceContext.displayName = "PresenceContext";

/**
 * Presence Provider Component
 * Manages Socket.IO connection and provides presence data to children
 */
export function PresenceProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  // Cleanup existing socket before creating new one
  const cleanupSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.off("connect");
      socketRef.current.off("connect_error");
      socketRef.current.off("disconnect");
      socketRef.current.off("reconnect");
      socketRef.current.off("reconnect_failed");
    }
    disconnectPresenceSocket();
    socketRef.current = null;
  }, []);

  // Initialize socket connection
  const initializeSocket = useCallback(() => {
    const token = getToken();

    if (!token) {
      return;
    }

    // Cleanup any existing socket first
    cleanupSocket();

    try {
      const socket = createPresenceSocket(token);

      if (!socket) {
        setError("Failed to create socket connection");
        return;
      }

      socketRef.current = socket;

      // Connection established
      socket.on("connect", () => {
        if (isMountedRef.current) {
          setIsConnected(true);
          setError(null);
        }
      });

      // Connection error handling
      socket.on("connect_error", (err) => {
        console.error("Presence socket connection error:", err.message);
        if (isMountedRef.current) {
          setIsConnected(false);
          if (err.message === "Unauthorized") {
            setError("Authentication failed. Please log in again.");
          } else {
            setError(`Connection error: ${err.message}`);
          }
        }
      });

      // Disconnection handling
      socket.on("disconnect", (reason) => {
        if (isMountedRef.current) {
          setIsConnected(false);
        }

        if (reason === "io server disconnect") {
          // Server disconnected us, try to reconnect after delay
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              initializeSocket();
            }
          }, 5000);
        }
      });

      // Reconnection events
      socket.on("reconnect", () => {
        if (isMountedRef.current) {
          setIsConnected(true);
          setError(null);
        }
      });

      // Handle permanent reconnection failure
      socket.on("reconnect_failed", () => {
        if (isMountedRef.current) {
          setError("Unable to reconnect. Please check your connection.");
        }
      });
    } catch (err) {
      console.error("Error initializing presence socket:", err);
      if (isMountedRef.current) {
        setError("Failed to initialize presence tracking");
      }
    }
  }, [cleanupSocket]);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    cleanupSocket();
    setError(null);
    initializeSocket();
  }, [initializeSocket, cleanupSocket]);

  // Initialize on mount and cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    // Check if token exists before initializing
    const token = getToken();
    if (token) {
      initializeSocket();
    }

    return () => {
      isMountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      cleanupSocket();
    };
  }, [initializeSocket, cleanupSocket]);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isConnected && getToken()) {
        reconnect();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isConnected, reconnect]);

  const value = {
    isConnected,
    error,
    reconnect,
  };

  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
}

PresenceProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Custom hook to access presence context
 * @returns {Object} Presence context value
 */
export function usePresence() {
  const context = useContext(PresenceContext);

  if (context === undefined) {
    throw new Error("usePresence must be used within a PresenceProvider");
  }

  return context;
}

export default PresenceContext;
